import { useCallback, useEffect, useState } from "react";

import { businessApi } from "../../../../services/api";
import { Button, ConfirmDialog, Dialog, DialogClose, Field, Input, Notice, Select, Textarea, cn, focusRing, plainError } from "../../../../components/ui";
import { formatPayDate } from "../compensationView";

const FREQUENCY_WORDS = { semi_monthly: "twice a month", bi_weekly: "every two weeks", weekly: "weekly", monthly: "once a month" };
/** Any date on or before the first day an employee can be paid is read by the server as that day. */
const FROM_FIRST_PAYABLE_DAY = "2000-01-01";

export const scheduleLabel = (schedule) => `${schedule.name} (${FREQUENCY_WORDS[schedule.frequency] || schedule.frequency})`;

function assignmentText(row) {
  const from = formatPayDate(row.effectiveFrom);
  return row.effectiveUntil ? `${from} – ${formatPayDate(row.effectiveUntil)}` : `Since ${from}`;
}

/** Put an employee who has no pay schedule on one. The server decides whether the start date is allowed. */
function AssignDialog({ memberId, memberName, schedules, onClose, onDone }) {
  const [scheduleId, setScheduleId] = useState(schedules.length === 1 ? String(schedules[0].id) : "");
  const [start, setStart] = useState("first");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const ready = Boolean(scheduleId) && (start === "first" || Boolean(date));

  const submit = async (event) => {
    event.preventDefault();
    if (busy || !ready) return;
    setBusy(true);
    setFailure("");
    try {
      const res = await businessApi.assignPaySchedule({
        memberId, scheduleId: Number(scheduleId), effectiveFrom: start === "first" ? FROM_FIRST_PAYABLE_DAY : date,
      });
      onDone(`${memberName} is on ${res?.data?.scheduleName || "the pay schedule"} from ${formatPayDate(res?.data?.effectiveFrom)}.`);
    } catch (error) {
      setFailure(plainError(error, "The pay schedule couldn't be saved. Try again."));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open && !busy) onClose(); }}
      title="Choose a pay schedule"
      description={`The pay schedule decides which payroll pays ${memberName} and on which dates.`}
      footer={
        <>
          <DialogClose asChild><Button disabled={busy}>Cancel</Button></DialogClose>
          <Button variant="primary" type="submit" form="assign-pay-schedule" loading={busy} disabled={!ready}>Save pay schedule</Button>
        </>
      }
    >
      <form id="assign-pay-schedule" noValidate onSubmit={submit} className="space-y-4">
        <Field label="Pay schedule" required>
          <Select value={scheduleId} disabled={busy} onChange={(event) => setScheduleId(event.target.value)}>
            {schedules.length === 1 ? null : <option value="">Choose a pay schedule</option>}
            {schedules.map((schedule) => <option key={schedule.id} value={schedule.id}>{scheduleLabel(schedule)}</option>)}
          </Select>
        </Field>
        <fieldset className="space-y-2">
          <legend className="text-label text-secondary">Starts</legend>
          {[
            ["first", "From their first day of pay", "Every day they have pay is covered."],
            ["later", "From a later pay period", "Days before it stay without a pay schedule."],
          ].map(([value, label, hint]) => (
            <label key={value} className="flex min-h-touch cursor-pointer items-start gap-3 rounded-control border border-default px-3 py-2.5">
              <input type="radio" name="assign-start" value={value} checked={start === value} disabled={busy} onChange={() => setStart(value)} className={cn("mt-1", focusRing)} />
              <span>
                <span className="block font-medium text-foreground">{label}</span>
                <span className="block text-meta text-secondary">{hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {start === "later" ? (
          <Field label="First day" required hint="The first day of a pay period of this schedule, for example the 1st or the 16th.">
            <Input type="date" value={date} disabled={busy} onChange={(event) => setDate(event.target.value)} />
          </Field>
        ) : null}
        {failure ? <Notice tone="danger" title="Pay schedule not saved" role="alert">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}

/**
 * Move an employee to another pay schedule. A transfer happens only where a period of the current schedule ends and
 * a period of the new one starts the next day; the server lists those dates and nothing else can be chosen.
 */
function TransferDialog({ memberId, memberName, current, schedules, onClose, onDone }) {
  const targets = schedules.filter((schedule) => schedule.id !== current.scheduleId);
  const [scheduleId, setScheduleId] = useState("");
  const [options, setOptions] = useState(null); // null | "loading" | { dates, reason }
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");

  // The dates depend on the schedule chosen, so they are fetched when it is chosen (not in an effect).
  const choose = async (value) => {
    setScheduleId(value);
    setDate("");
    setFailure("");
    if (!value) { setOptions(null); return; }
    setOptions("loading");
    try {
      const res = await businessApi.getPayScheduleTransferOptions(memberId, value);
      const dates = res?.data?.dates || [];
      setOptions({ dates, reason: res?.data?.reason || "" });
      if (dates.length === 1) setDate(dates[0]);
    } catch (error) {
      setOptions({ dates: [], reason: plainError(error, "The transfer dates couldn't be loaded. Try again.") });
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy || !date) return;
    setBusy(true);
    setFailure("");
    try {
      const res = await businessApi.transferPaySchedule({ memberId, toScheduleId: Number(scheduleId), effectiveFrom: date, reason: reason.trim() || null });
      const stale = res?.data?.periodsToRecalculate || [];
      onDone(
        `${memberName} moves to ${res?.data?.assignment?.scheduleName || "the new pay schedule"} on ${formatPayDate(date)}.`
          + (stale.length ? ` Recalculate ${stale.map((item) => item.name).join(", ")}: it was calculated before this change.` : ""),
      );
    } catch (error) {
      setFailure(plainError(error, "The transfer couldn't be saved. Try again."));
      setBusy(false);
    }
  };

  const loaded = options && options !== "loading" ? options : null;

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open && !busy) onClose(); }}
      title="Move to another pay schedule"
      description={`${memberName} is on ${current.scheduleName}. They move on a day one pay period ends and the other begins, so no day is unpaid or paid twice.`}
      footer={
        <>
          <DialogClose asChild><Button disabled={busy}>Cancel</Button></DialogClose>
          <Button variant="primary" type="submit" form="transfer-pay-schedule" loading={busy} disabled={!date}>Move employee</Button>
        </>
      }
    >
      <form id="transfer-pay-schedule" noValidate onSubmit={submit} className="space-y-4">
        <Field label="New pay schedule" required>
          <Select value={scheduleId} disabled={busy} onChange={(event) => choose(event.target.value)}>
            <option value="">Choose a pay schedule</option>
            {targets.map((schedule) => <option key={schedule.id} value={schedule.id}>{scheduleLabel(schedule)}</option>)}
          </Select>
        </Field>
        {options === "loading" ? <p className="text-body-sm text-tertiary">Finding the dates they can move…</p> : null}
        {loaded && loaded.dates.length ? (
          <fieldset>
            <legend className="mb-1.5 text-label text-secondary">First day on the new schedule</legend>
            <ul className="divide-y divide-subtle rounded-panel border border-default">
              {loaded.dates.map((value) => (
                <li key={value}>
                  <label className="flex min-h-touch cursor-pointer items-center gap-3 px-3 py-2.5">
                    <input type="radio" name="transfer-date" value={value} checked={date === value} disabled={busy} onChange={() => setDate(value)} className={focusRing} />
                    <span className="font-medium text-foreground">{formatPayDate(value)}</span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        ) : null}
        {loaded && !loaded.dates.length ? <Notice tone="warning" title="No date works for these two schedules">{loaded.reason}</Notice> : null}
        {loaded && loaded.dates.length ? (
          <Field label="Reason (optional)">
            <Textarea rows={2} maxLength={500} value={reason} disabled={busy} onChange={(event) => setReason(event.target.value)} />
          </Field>
        ) : null}
        {failure ? <Notice tone="danger" title="Not moved" role="alert">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}

/**
 * HR → Employee → Pay: the pay schedule that pays this employee (Payroll Gate 2). The server says what the viewer
 * may do (`canManage`) and which schedules and transfer dates exist; this panel only shows and asks.
 */
export default function EmployeePaySchedulePanel({ memberId, memberName, former = false, refreshKey = 0, showToast }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | hidden | error
  const [dialog, setDialog] = useState(null); // "assign" | "transfer"
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    if (!memberId) return;
    try {
      const res = await businessApi.getMemberPaySchedule(memberId);
      setData(res?.data || null);
      setState("ready");
    } catch (error) {
      // Someone who cannot read payroll simply does not see the section.
      setState(error?.response?.status === 403 || error?.status === 403 ? "hidden" : "error");
    }
  }, [memberId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (state === "hidden") return null;
  if (state === "loading") return <p className="text-body-sm text-tertiary">Loading pay schedule…</p>;
  if (state === "error" || !data) {
    return (
      <Notice tone="danger" title="The pay schedule did not load">
        <Button size="sm" onClick={() => { setState("loading"); load(); }}>Try again</Button>
      </Notice>
    );
  }

  const { current, upcoming, unassigned, schedules = [], canManage, history = [] } = data;
  const canEdit = canManage && !former;
  const shown = current || upcoming;
  const others = schedules.filter((schedule) => schedule.id !== shown?.scheduleId);
  const past = history.filter((row) => !row.cancelledAt && row.id !== current?.id && row.id !== upcoming?.id);
  const done = async (message) => {
    setDialog(null);
    showToast?.("success", message);
    await load();
  };

  return (
    <div className="space-y-4">
      {current ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-body font-medium text-foreground">{current.scheduleName}</p>
            <p className="text-meta text-secondary">
              Paid {FREQUENCY_WORDS[current.scheduleFrequency] || current.scheduleFrequency} · {assignmentText(current)} · {current.sourceText}
            </p>
          </div>
          {canEdit && !upcoming && others.length ? <Button onClick={() => setDialog("transfer")}>Move to another pay schedule</Button> : null}
        </div>
      ) : (
        <div className="space-y-3">
          {unassigned ? (
            <Notice tone="warning" title="Not on a pay schedule">
              {memberName} has pay but no pay schedule, so no payroll pays them and payroll cannot be sent to Finance.
              {canEdit ? "" : " Someone who manages payroll can choose one."}
            </Notice>
          ) : (
            <p className="text-body-sm text-secondary">
              {upcoming ? "No pay schedule today." : "No pay schedule yet. One is chosen when their pay is set."}
            </p>
          )}
          {canEdit && !upcoming && schedules.length ? <Button onClick={() => setDialog("assign")}>Choose a pay schedule</Button> : null}
          {canEdit && !schedules.length ? <p className="text-body-sm text-secondary">This business has no active pay schedule. The business owner activates one.</p> : null}
        </div>
      )}

      {upcoming ? (
        <div className="flex flex-col gap-3 rounded-panel border border-default px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-body-sm text-foreground">
            {current ? "Moves to" : "Starts on"} <span className="font-medium">{upcoming.scheduleName}</span> on {formatPayDate(upcoming.effectiveFrom)}.
          </p>
          {canEdit ? <Button size="sm" onClick={() => setCancelling(upcoming)}>{current ? "Cancel this move" : "Cancel"}</Button> : null}
        </div>
      ) : null}

      {past.length ? (
        <section className="space-y-2">
          <h3 className="text-body font-semibold text-foreground">Earlier pay schedules</h3>
          <ul aria-label="Earlier pay schedules" className="divide-y divide-subtle rounded-panel border border-default">
            {past.map((row) => (
              <li key={row.id} className="px-3 py-2.5">
                <p className="text-body-sm font-medium text-foreground">{row.scheduleName}</p>
                <p className="text-meta text-secondary">{assignmentText(row)}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {dialog === "assign" ? (
        <AssignDialog memberId={memberId} memberName={memberName} schedules={schedules} onClose={() => setDialog(null)} onDone={done} />
      ) : null}
      {dialog === "transfer" && current ? (
        <TransferDialog memberId={memberId} memberName={memberName} current={current} schedules={schedules} onClose={() => setDialog(null)} onDone={done} />
      ) : null}
      {cancelling ? (
        <ConfirmDialog
          title={current ? `Cancel the move to ${cancelling.scheduleName}?` : `Cancel the start on ${cancelling.scheduleName}?`}
          facts={[{ label: "Was to start", value: formatPayDate(cancelling.effectiveFrom) }]}
          consequence={current ? `${memberName} stays on ${current.scheduleName}.` : `${memberName} will have no pay schedule until one is chosen.`}
          confirmLabel={current ? "Cancel the move" : "Cancel the start"}
          cancelLabel="Keep it"
          tone="danger"
          onConfirm={async () => {
            await businessApi.cancelPayScheduleAssignment(cancelling.id);
            setCancelling(null);
            showToast?.("success", current ? "The move was cancelled." : "The pay schedule start was cancelled.");
            await load();
          }}
          onClose={() => setCancelling(null)}
        />
      ) : null}
    </div>
  );
}
